import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RolesService } from '../../roles/roles.service';
import { LocationsService } from '../../locations/locations.service';
import { DbService } from 'src/app/services/db-service/db.service';
import { SeoImageUploadService } from 'src/app/services/ImageUploadService/seo-image-upload.service';
import { Router } from '@angular/router';
import { BusinessService } from '../../business/business.service';
import { TemplateService } from 'src/app/services/template.service';
import { MediaSelectorComponent } from '../../apps/template/templates/popups/media-selector/media-selector.component';

interface Goal {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

export interface TemplateDataFor {
  id: string;
  title: string;
  description: string;
  imageUrl: string[];
  tags: string[];
  specifications: string;
  callToAction: string;
  bestChannels: string[];
  templateText: string;
  exampleText: string;
  framework: string;
  goals: any[];
  platform: string[];
  category: Category | null;
  selectedIndustry: { id: string; name: string } | null;
  selectedSubIndustry: { id: string; name: string } | null;
  hookStrategy: string;
  contentStructure: string;
  seasons: any[];
}

@Component({
  selector: 'app-add-template',
  templateUrl: './add-template.component.html',
  styleUrls: ['./add-template.component.scss']
})
export class AddTemplateComponent implements OnInit {
  currentStep: number = 1;
  isEditMode = false;
  templateId: string | null = null;
  activeSubIndustries: any;

  templateData: TemplateDataFor = {
    id: '',
    title: '',
    description: '',
    specifications: '',
    callToAction: '',
    bestChannels: [],
    goals: [],
    platform: [],
    tags: [],
    templateText: '',
    exampleText: '',
    framework: '',
    imageUrl: [],
    category: null,
    selectedIndustry: null,
    selectedSubIndustry: null,
    hookStrategy: '',
    contentStructure: '',
    seasons: []
  };

  categoriesData: Category[] = [];
  selectedCategory: Category | null = null;
  selectedSeasons: any[] = []
  goalsData: Goal[] = [];
  newTag = '';
  isUploading = false;
  platformsData: string[] = [];
  selectedGoalIds: string[] = [];
  selectedPlatform: string | null = null;
  listSeasons: any = [
    { value: 'SANKRANTI', viewValue: 'Sankranti' },
    { value: 'UGADI', viewValue: 'Ugadi' },
    { value: 'GANESH_CHATURTHI', viewValue: 'Ganesh Chaturthi' },
    { value: 'CHRISTMAS', viewValue: 'Christmas' },
    { value: 'DIWALI', viewValue: 'Diwali' },
    { value: 'SUMMER', viewValue: 'Summer Season' },
    { value: 'WINTER', viewValue: 'Winter Season' },
    { value: 'MONSOON', viewValue: 'Monsoon Season' },
    { value: 'WEDDING_SEASON', viewValue: 'Wedding Season' },
    { value: 'NEW_YEAR', viewValue: 'New Year' },
    { value: 'END_OF_SEASON_SALE', viewValue: 'End of Season Sale' },
    { value: 'TRAVEL_VACATION', viewValue: 'Travel & Vacation Season' },
  ]

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private rolesService: RolesService,
    private locationService: LocationsService,
    private dbService: DbService,
    private _seoImageUploadService: SeoImageUploadService,
    private dialogRef: MatDialogRef<AddTemplateComponent>,
    private router: Router,
    private businessService: BusinessService,
    private templateService: TemplateService
  ) { }

  ngOnInit() {
    this.getAllGoalListing();
    this.getAllCategorisListing();
    this.fetchPlatforms();
    this.getActiveSubIndustries();

    if (this.data?.isEditMode && this.data.template) {
      this.isEditMode = true;
      this.templateId = this.data.template.id;
      this.prefillTemplate(this.data.template);
    }
  }

  // ------------------ Step Validations ------------------
  isStep1Valid(): boolean {
    return !!(
      this.templateData.title?.trim() &&
      this.templateData.description?.trim() &&
      Array.isArray(this.templateData.imageUrl) && this.templateData.imageUrl.length > 0 &&
      this.templateData.selectedSubIndustry &&
      this.templateData.category
    );
  }

  isStep4Valid(): boolean {
    return !!(
      this.templateData.title?.trim() &&
      this.templateData.imageUrl?.length > 0 &&
      this.selectedCategory
    );
  }

  prefillTemplate(template: any) {
    this.templateData = {
      id: template.id,
      title: template.title || '',
      description: template.description || '',
      imageUrl: template.images || [],
      tags: template.tags || [],
      specifications: template.specification || '',
      callToAction: template.cta || '',
      bestChannels: template.bestChannels || [],
      templateText: template.rawTemplate || '',
      exampleText: template.templateExample || '',
      hookStrategy: template.hookStrategy || '',
      framework: Array.isArray(template.contentStructure) ? template.contentStructure.join('\n') : (template.contentStructure || ''),
      contentStructure: Array.isArray(template.contentStructure) ? template.contentStructure.join('\n') : (template.contentStructure || ''),
      goals: template.socialGoals || [],
      platform: template.platform || [],
      selectedIndustry: template.industry || null,
      selectedSubIndustry: template.subIndustry || null,
      category: template.socialCategory || null,
      seasons: template.seasons || [],
    };

    // sync the separate UI-bound variables
    this.selectedCategory = template.socialCategory || null;
    this.selectedSeasons = template.seasons || [];

    // preselect goals
    if (template.socialGoals?.length) {
      this.selectedGoalIds = template.socialGoals.map((g: Goal) => g.id);
    }
  }

  // ------------------ Goals ------------------
  getAllGoalListing() {
    const data = { pageNo: 0, size: 100000, searchText: '' };
    this.rolesService.getGoalsList(data).subscribe({
      next: (res: any) => {
        this.goalsData = res?.data?.data || [];
        if (this.isEditMode && this.selectedGoalIds.length) {
          this.onGoalsChange(this.selectedGoalIds);
        }
      },
      error: (err: any) => {
        console.error('Error fetching goals:', err);
        this.goalsData = [];
      }
    });
  }

  onGoalsChange(goalIds: string[]) {
    this.selectedGoalIds = goalIds;
    this.templateData.goals = this.goalsData.filter(g => goalIds.includes(g.id));
  }

  removeGoal(goalId: string) {
    this.selectedGoalIds = this.selectedGoalIds.filter(id => id !== goalId);
    this.templateData.goals = this.templateData.goals.filter(g => g.id !== goalId);
  }

  // ------------------ Categories ------------------
  getAllCategorisListing() {
    const data = { pageNo: 0, size: 10, searchText: '' };
    this.rolesService.getCategoryList(data).subscribe({
      next: (res: any) => {
        this.categoriesData = res?.data?.data || [];
      },
      error: (err: any) => {
        console.error('Error fetching categories:', err);
        this.categoriesData = [];
      }
    });
  }

  onCategoryChange(categoryOrId: any) {
    let category: Category | null = null;

    if (!categoryOrId) {
      category = null;
    } else if (typeof categoryOrId === 'string') {
      category = this.categoriesData.find(c => c.id === categoryOrId) || null;
    } else if (typeof categoryOrId === 'object') {
      category = this.categoriesData.find(c => c.id === categoryOrId.id) || categoryOrId;
    }

    this.selectedCategory = category;
    this.templateData.category = category;
  }

  onSeasonChange() {

  }

  compareSubIndustries(sub1: any, sub2: any): boolean {
    return sub1 && sub2 ? sub1.id === sub2.id : sub1 === sub2;
  }

  onSubIndustryChange(subIndustry: any) {
    this.templateData.selectedSubIndustry = subIndustry;
  }

  fetchPlatforms() {
    this.rolesService.getPlatformsList().subscribe((res) => {
      this.platformsData = res.data || [];
    });
  }

  selectPlatform(platform: string) {
    if (platform && !this.templateData.platform.includes(platform)) {
      this.templateData.platform.push(platform);
      this.selectedPlatform = platform;
    }
  }

  removePlatform(platform: string) {
    this.templateData.platform = this.templateData.platform.filter(p => p !== platform);
  }

  addTag() {
    if (this.newTag && !this.templateData.tags.includes(this.newTag)) {
      this.templateData.tags.push(this.newTag);
      this.newTag = '';
    }
  }

  removeTag(tag: string) {
    this.templateData.tags = this.templateData.tags.filter(t => t !== tag);
  }

  // ------------------ Media Upload ------------------
  async onFilesSelected(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;

    this.isUploading = true;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileType = file.type;

      if (!fileType.startsWith('image/') && !fileType.startsWith('video/')) {
        alert('Only image or video files are allowed.');
        continue;
      }

      try {
        const uploadedUrl: any = await this._seoImageUploadService.uploadSEOimages(file, 'Blogs');
        this.templateData.imageUrl.push(uploadedUrl.Location);
      } catch (error) {
        console.error('Upload failed for file', file.name, error);
        alert(`Failed to upload ${file.name}`);
      }
    }

    this.isUploading = false;
  }

  openMediaSelector() {
    const height = window.innerWidth > 700 ? '90vh' : '100vh';
    const width = window.innerWidth > 700 ? '50%' : '100%';

    const dialog = this.templateService.openDialogWithData(
      MediaSelectorComponent,
      height,
      width,
      { multiple: true, type: "smm-post-image" }
    );

    dialog.afterClosed().subscribe((selectedMedia: any[]) => {
      if (selectedMedia && selectedMedia.length > 0) {
        selectedMedia.forEach((mediaData: any) => {
          if (mediaData.assets && mediaData.assets.length > 0) {
            const mediaUrl = mediaData.assets[0].url;

            // Check for duplicates
            const isDuplicate = this.templateData.imageUrl.some(
              (item: string) => item === mediaUrl
            );

            if (!isDuplicate) {
              this.templateData.imageUrl.push(mediaUrl);
            }
          }
        });

        this.locationService.openSnack('Media added successfully!', 'close');
      }
    });
  }

  removeMedia(mediaUrl: string) {
    this.templateData.imageUrl = this.templateData.imageUrl.filter(url => url !== mediaUrl);
  }

  isImage(url: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  }

  isVideo(url: string): boolean {
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  updateTemplate() {
    if (!this.templateId) {
      console.error('No template ID found for update.');
      return;
    }

    if (!this.templateData.title || !this.selectedCategory) {
      this.locationService.openSnack('Please enter title and select category', 'close');
      return;
    }

    const payload = {
      id: this.templateData.id,
      title: this.templateData.title,
      description: this.templateData.description,
      images: this.templateData.imageUrl ? this.templateData.imageUrl : [],
      tags: this.templateData.tags,
      specification: this.templateData.specifications,
      cta: this.templateData.callToAction,
      industry: this.templateData.selectedIndustry ? { id: this.templateData.selectedIndustry.id, name: this.templateData.selectedIndustry.name } : { id: null, name: null },
      subIndustry: this.templateData.selectedSubIndustry ? { id: this.templateData.selectedSubIndustry.id, name: this.templateData.selectedSubIndustry.name } : { id: null, name: null },
      socialCategory: this.selectedCategory,
      socialGoals: this.templateData.goals,
      platform: this.templateData.platform,
      rawTemplate: this.templateData.templateText,
      templateExample: this.templateData.exampleText,
      hookStrategy: this.templateData.hookStrategy || '',
      contentStructure: this.templateData.contentStructure ? [this.templateData.contentStructure] : [],
      callToAction: this.templateData.callToAction,
      lastUpdatedBy: {
        id: this.dbService.getStaffId(),
        name: this.dbService.getStaffName()
      },
      updatedAt: new Date().toISOString(),
      active: true,
      seasons: (this.selectedSeasons || []).map((s: any) => String(s).toUpperCase())
    };

    this.rolesService.updateTemplate(payload).subscribe({
      next: (res: any) => {
        this.locationService.openSnack('Template updated successfully', 'close');
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.locationService.openSnack(err.error.message || 'Error updating template', 'close');
        console.error('API Error:', err);
      }
    });
  }

  createTemplate() {
    if (!this.templateData.title || !this.selectedCategory) {
      this.locationService.openSnack('Please enter title and select category', 'close');
      return;
    }

    const payload = {
      title: this.templateData.title,
      description: this.templateData.description,
      images: this.templateData.imageUrl ? this.templateData.imageUrl : [],
      tags: this.templateData.tags,
      specification: this.templateData.specifications,
      cta: this.templateData.callToAction,
      industry: this.templateData.selectedIndustry,
      subIndustry: this.templateData.selectedSubIndustry,
      socialCategory: this.selectedCategory,
      socialGoals: this.templateData.goals,
      platform: this.templateData.platform,
      rawTemplate: this.templateData.templateText,
      templateExample: this.templateData.exampleText,
      hookStrategy: this.templateData.hookStrategy,
      contentStructure: this.templateData.contentStructure ? [this.templateData.contentStructure] : [],
      callToAction: this.templateData.callToAction,
      createdBy: {
        id: this.dbService.getStaffId(),
        name: this.dbService.getStaffName()
      },
      lastUpdatedBy: {
        id: this.dbService.getStaffId(),
        name: this.dbService.getStaffName()
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      active: true,
      seasons: (this.selectedSeasons || []).map((s: any) => String(s).toUpperCase())
    };

    this.rolesService.createTemplate(payload).subscribe({
      next: (res: any) => {
        this.locationService.openSnack('Template created successfully', 'close');
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.locationService.openSnack(err.error.message || 'Error saving template', 'close');
        console.error('API Error:', err);
      }
    });
  }

  nextStep() {
    if (this.currentStep < 4) this.currentStep++;
  }

  previousStep() {
    if (this.currentStep > 1) this.currentStep--;
  }

  goToStep(step: number) {
    this.currentStep = step;
  }

  getActiveSubIndustries() {
    let payload = {
      pageNo: 0,
      size: 30,
      industryIds: [],
      active: true
    }
    this.businessService.subIndustryFilter(payload).subscribe((res: any) => {
      this.activeSubIndustries = res.data.data;
    });
  }

  closePopup() {
    this.dialogRef.close();
  }
}